/**
 * LE POSTE CLIENT ET LES ENCAISSEMENTS, LUS CHEZ PENNYLANE — lot 158, 03/11/2026.
 *
 * Planches « Recouvrement · 4 » et « · 5 » validées le 03/11 (« ok bien »),
 * avec les deux ajouts gardés : le détail des factures au clic sur un client
 * et le retard moyen au paiement.
 *
 * Les deux écrans se relisent quand le bandeau Pennylane a fini sa lecture
 * (`version`, incrémentée par la Comptabilité) : ce qu'ils montrent vient de
 * la copie que ce bandeau tient à jour.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import { lireEncaissements, lirePoste } from './apiRecouvrementPl.js';
import { lireFileRelances } from './apiRelances.js';
import {
  balanceAgeePl,
  chiffresEncaissements,
  chiffresPoste,
  encaissementsEntre,
  lesPlusEchus,
  plusGrosRetard,
  ecartDit,
  filtrerEncaissements,
  filtrerPoste,
  periodeParDefaut,
  type ClientDuPoste,
  type Encaissements,
  type Poste,
} from './recouvrementPl.js';
import {
  dateDite,
  decaler,
  grouper,
  montantDit,
  NOMS_NIVEAUX,
  type FileRelances,
  type NiveauPl,
} from './relancesPl.js';

interface ProprietesEcran {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly version: number;
}

function Niveau({ niveau }: { readonly niveau: NiveauPl | null }) {
  if (niveau === null) return <span className="rp-vide">—</span>;
  return <span className={`rp-niveau rp-${niveau}`}>{NOMS_NIVEAUX[niveau]}</span>;
}

const euros = (centimes: number) => `${montantDit(centimes / 100)} €`;

// ---------------------------------------------------------------------------
// LE POSTE CLIENT

export function VuePostePl(p: {
  readonly poste: Poste | null;
  readonly refus: string | null;
  readonly recherche: string;
  readonly seulementEnRetard: boolean;
  readonly ouvert: string | null;
  readonly quandRecherche: (t: string) => void;
  readonly quandRetard: (v: boolean) => void;
  readonly quandOuvrir: (cle: string | null) => void;
}) {
  if (p.poste === null) {
    return p.refus === null ? (
      <p className="attente">Chargement du poste client…</p>
    ) : (
      <p className="bandeau-refus">{p.refus}</p>
    );
  }
  const c = chiffresPoste(p.poste.clients);
  const vus = filtrerPoste(p.poste.clients, p.recherche, p.seulementEnRetard);
  return (
    <div className="rp-ecran">
      {p.refus !== null && <p className="bandeau-refus">{p.refus}</p>}
      <div className="rp-chiffres ep-trois">
        <div className="rp-chiffre">
          <span>Total dû</span>
          <b>{euros(c.total_centimes)}</b>
        </div>
        <div className="rp-chiffre">
          <span>dont échu</span>
          <b className={c.echu_centimes > 0 ? 'rp-rouge' : ''}>{euros(c.echu_centimes)}</b>
        </div>
        <div className="rp-chiffre">
          <span>Clients en retard</span>
          <b>
            {c.en_retard} sur {c.clients}
          </b>
        </div>
      </div>
      <div className="barre-outils">
        <input
          className="recherche"
          type="search"
          placeholder="Client, facture…"
          aria-label="Rechercher un client"
          value={p.recherche}
          onChange={(e) => p.quandRecherche(e.target.value)}
        />
        <label className="ep-case">
          <input
            type="checkbox"
            checked={p.seulementEnRetard}
            onChange={(e) => p.quandRetard(e.target.checked)}
          />
          Seulement les clients en retard
        </label>
        {(p.recherche !== '' || p.seulementEnRetard) && (
          <span className="rp-compte">
            {vus.length} sur {p.poste.clients.length}
          </span>
        )}
      </div>
      {vus.length === 0 ? (
        <p className="rp-vide-file">Aucun client à afficher.</p>
      ) : (
        <div className="rp-cadre">
          <table className="rp-table">
            <thead>
              <tr>
                <th>Client</th>
                <th className="n">Factures ouvertes</th>
                <th className="n">Total dû TTC</th>
                <th className="n">Dont échu</th>
                <th>Plus ancienne échéance</th>
                <th>Dernière relance</th>
              </tr>
            </thead>
            <tbody>
              {vus.map((cl) => (
                <LigneClient key={cl.cle} cl={cl} ouvert={p.ouvert === cl.cle} quandOuvrir={p.quandOuvrir} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LigneClient({
  cl,
  ouvert,
  quandOuvrir,
}: {
  readonly cl: ClientDuPoste;
  readonly ouvert: boolean;
  readonly quandOuvrir: (cle: string | null) => void;
}) {
  return (
    <Fragment>
      <tr className={ouvert ? 'ep-ouvert' : undefined}>
        <td className="rp-client">
          <button
            className="ep-client"
            type="button"
            aria-expanded={ouvert}
            onClick={() => quandOuvrir(ouvert ? null : cl.cle)}
          >
            <span className="ep-fleche" aria-hidden="true">
              {ouvert ? '▾' : '▸'}
            </span>
            {cl.nom}
          </button>
        </td>
        <td className="n">{cl.factures.length}</td>
        <td className="n">{euros(cl.total_centimes)}</td>
        <td className="n">
          {cl.echu_centimes > 0 ? (
            <span className="rp-refus">{euros(cl.echu_centimes)}</span>
          ) : (
            <span className="rp-vide">{euros(0)}</span>
          )}
        </td>
        <td>{cl.plus_ancienne === null ? '—' : dateDite(cl.plus_ancienne)}</td>
        <td>
          <Niveau niveau={cl.derniere?.niveau ?? null} />
          {cl.derniere !== null && <span className="rp-sous">{dateDite(cl.derniere.envoyee_le)}</span>}
        </td>
      </tr>
      {ouvert &&
        cl.factures.map((f) => (
          <tr key={f.pennylane_facture_id} className="ep-facture">
            <td className="ep-numero">{f.numero ?? '—'}</td>
            <td />
            <td className="n">{montantDit(Number(f.reste_du))} €</td>
            <td className="n">
              {f.echue ? `${String(f.retard_jours)} j` : 'à échoir'}
              {f.gel !== null && <span className="rp-sous">gelée</span>}
            </td>
            <td>{f.echeance === null ? '—' : dateDite(f.echeance)}</td>
            <td>
              <Niveau niveau={f.derniere?.niveau ?? null} />
              {f.derniere !== null && <span className="rp-sous">{dateDite(f.derniere.envoyee_le)}</span>}
            </td>
          </tr>
        ))}
    </Fragment>
  );
}

export function EcranPostePl({
  jeton,
  quandAccesPerdu,
  version,
  ouvertAuDepart = null,
}: ProprietesEcran & { readonly ouvertAuDepart?: string | null }) {
  const [poste, setPoste] = useState<Poste | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [recherche, setRecherche] = useState('');
  const [retard, setRetard] = useState(false);
  // LOT 159 : un clic sur un client du Suivi ouvre le poste sur ce client.
  const [ouvert, setOuvert] = useState<string | null>(ouvertAuDepart);

  const relire = useCallback(async () => {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return;
    }
    const r = await lirePoste(j);
    if (r.etat === 'ok') {
      setPoste(r.valeur);
      setRefus(null);
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? 'Le poste client n’a pas pu être lu.');
    }
  }, [jeton, quandAccesPerdu]);

  useEffect(() => {
    void relire();
  }, [relire, version]);

  return (
    <VuePostePl
      poste={poste}
      refus={refus}
      recherche={recherche}
      seulementEnRetard={retard}
      ouvert={ouvert}
      quandRecherche={setRecherche}
      quandRetard={setRetard}
      quandOuvrir={setOuvert}
    />
  );
}

// ---------------------------------------------------------------------------
// LES ENCAISSEMENTS

export function VueEncaissementsPl(p: {
  readonly donnees: Encaissements | null;
  readonly refus: string | null;
  readonly du: string;
  readonly au: string;
  readonly recherche: string;
  readonly quandPeriode: (du: string, au: string) => void;
  readonly quandRecherche: (t: string) => void;
}) {
  const lignes = p.donnees === null ? [] : filtrerEncaissements(p.donnees.encaissements, p.recherche);
  const c = chiffresEncaissements(p.donnees?.encaissements ?? []);
  return (
    <div className="rp-ecran">
      <div className="barre-outils">
        <label className="ep-date">
          Du
          <input
            className="recherche"
            type="date"
            value={p.du}
            max={p.au}
            onChange={(e) => {
              if (e.target.value !== '') p.quandPeriode(e.target.value, p.au);
            }}
          />
        </label>
        <label className="ep-date">
          au
          <input
            className="recherche"
            type="date"
            value={p.au}
            min={p.du}
            onChange={(e) => {
              if (e.target.value !== '') p.quandPeriode(p.du, e.target.value);
            }}
          />
        </label>
        <input
          className="recherche"
          type="search"
          placeholder="Client, facture…"
          aria-label="Rechercher un encaissement"
          value={p.recherche}
          onChange={(e) => p.quandRecherche(e.target.value)}
        />
        {p.donnees !== null && p.recherche !== '' && (
          <span className="rp-compte">
            {lignes.length} sur {p.donnees.encaissements.length}
          </span>
        )}
      </div>
      {p.refus !== null && <p className="bandeau-refus">{p.refus}</p>}
      {p.donnees === null ? (
        p.refus === null && <p className="attente">Chargement des encaissements…</p>
      ) : (
        <>
          <div className="rp-chiffres ep-trois">
            <div className="rp-chiffre">
              <span>Encaissé sur la période</span>
              <b>{euros(c.encaisse_centimes)}</b>
            </div>
            <div className="rp-chiffre">
              <span>Factures réglées</span>
              <b>{c.reglees}</b>
            </div>
            <div className="rp-chiffre">
              <span>Retard moyen au paiement</span>
              <b className={c.retard_moyen !== null && c.retard_moyen > 0 ? 'ep-ambre' : ''}>
                {c.retard_moyen === null ? '—' : `${String(c.retard_moyen)} j`}
              </b>
            </div>
          </div>
          {p.donnees.sans_date > 0 && (
            <p className="ep-attente">
              {p.donnees.sans_date} facture(s) payée(s) sans date de règlement connue chez Pennylane.
            </p>
          )}
          {lignes.length === 0 ? (
            <p className="rp-vide-file">Aucun encaissement sur la période.</p>
          ) : (
            <div className="rp-cadre">
              <table className="rp-table">
                <thead>
                  <tr>
                    <th>Réglée le</th>
                    <th>Client</th>
                    <th>Facture</th>
                    <th>Échéance</th>
                    <th className="n">Montant TTC</th>
                    <th className="n">Par rapport à l’échéance</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => {
                    const e = ecartDit(l.ecart_jours);
                    return (
                      <tr key={l.pennylane_facture_id}>
                        <td>{dateDite(l.reglee_le)}</td>
                        <td className="rp-client">{l.client}</td>
                        <td>{l.numero ?? '—'}</td>
                        <td>{l.echeance === null ? '—' : dateDite(l.echeance)}</td>
                        <td className="n">{montantDit(Number(l.montant_ttc))} €</td>
                        <td className="n">
                          <span className={`ep-${e.ton}`}>{e.texte}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function EcranEncaissementsPl({
  jeton,
  quandAccesPerdu,
  version,
  aujourdhui,
}: ProprietesEcran & { readonly aujourdhui: string }) {
  const [periode, setPeriode] = useState(() => periodeParDefaut(aujourdhui));
  const [donnees, setDonnees] = useState<Encaissements | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [recherche, setRecherche] = useState('');

  const relire = useCallback(async () => {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return;
    }
    const r = await lireEncaissements(j, periode.du, periode.au);
    if (r.etat === 'ok') {
      setDonnees(r.valeur);
      setRefus(null);
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? 'Les encaissements n’ont pas pu être lus.');
    }
  }, [jeton, quandAccesPerdu, periode]);

  useEffect(() => {
    void relire();
  }, [relire, version]);

  return (
    <VueEncaissementsPl
      donnees={donnees}
      refus={refus}
      du={periode.du}
      au={periode.au}
      recherche={recherche}
      quandPeriode={(du, au) => setPeriode({ du, au })}
      quandRecherche={setRecherche}
    />
  );
}

// ---------------------------------------------------------------------------
// LE SUIVI — lot 159, 03/11/2026 (planche « Recouvrement · 6 », validée le 03/11)

export interface DonneesSuivi {
  readonly poste: Poste;
  readonly relances: FileRelances;
  /** Du plus tôt du mois et des 30 derniers jours, jusqu'à aujourd'hui. */
  readonly encaissements: Encaissements;
}

const COULEURS_TRANCHES = ['#c3cdea', '#e8b84a', '#d9822b', '#b8452f', '#8f2a2a', '#5e1a1a'];

export function VueSuiviPl(p: {
  readonly donnees: DonneesSuivi | null;
  readonly refus: string | null;
  readonly quandAller: (onglet: 'poste' | 'relances' | 'encaissements', client?: string) => void;
}) {
  if (p.donnees === null) {
    return p.refus === null ? (
      <p className="attente">Chargement du suivi…</p>
    ) : (
      <p className="bandeau-refus">{p.refus}</p>
    );
  }
  const { poste, relances, encaissements } = p.donnees;
  const jour = poste.jour;
  const cp = chiffresPoste(poste.clients);
  const g = grouper(relances.lignes);
  const duMois = encaissementsEntre(encaissements.encaissements, `${jour.slice(0, 7)}-01`, jour);
  const trente = encaissementsEntre(encaissements.encaissements, decaler(jour, -30), jour);
  const ceMois = chiffresEncaissements(duMois);
  const retard = chiffresEncaissements(trente).retard_moyen;
  const tranches = balanceAgeePl(poste.clients);
  const premiers = lesPlusEchus(poste.clients, 5);
  const aEnvoyer = g.retard.length + g.jour.length;

  const tuiles: readonly {
    readonly titre: string;
    readonly valeur: string;
    readonly note: string;
    readonly vers: 'poste' | 'relances' | 'encaissements';
    readonly nomVers: string;
    readonly ton: string;
  }[] = [
    {
      titre: 'Échu',
      valeur: euros(cp.echu_centimes),
      note: `sur ${euros(cp.total_centimes)} dû`,
      vers: 'poste',
      nomVers: 'Poste client',
      ton: cp.echu_centimes > 0 ? 'rp-rouge' : '',
    },
    {
      titre: 'Relances à envoyer',
      valeur: String(aEnvoyer),
      note: [
        `dont ${String(g.retard.length)} en retard d’envoi`,
        g.gelees.length > 0 ? `${String(g.gelees.length)} gelée${g.gelees.length > 1 ? 's' : ''}` : null,
      ]
        .filter((t) => t !== null)
        .join(' · '),
      vers: 'relances',
      nomVers: 'Relances',
      ton: '',
    },
    {
      titre: 'Encaissé ce mois-ci',
      valeur: euros(ceMois.encaisse_centimes),
      note: `${String(ceMois.reglees)} facture${ceMois.reglees > 1 ? 's réglées' : ' réglée'}`,
      vers: 'encaissements',
      nomVers: 'Encaissements',
      ton: '',
    },
    {
      titre: 'Retard moyen au paiement',
      valeur: retard === null ? '—' : `${String(retard)} j`,
      note: 'sur les 30 derniers jours',
      vers: 'encaissements',
      nomVers: 'Encaissements',
      ton: retard !== null && retard > 0 ? 'ep-ambre' : '',
    },
  ];

  return (
    <div className="rp-ecran">
      {p.refus !== null && <p className="bandeau-refus">{p.refus}</p>}
      <div className="rp-chiffres">
        {tuiles.map((t) => (
          <button key={t.titre} type="button" className="rp-chiffre ep-tuile" onClick={() => p.quandAller(t.vers)}>
            <span>{t.titre}</span>
            <b className={t.ton}>{t.valeur}</b>
            <span className="ep-note">{t.note}</span>
            <span className="ep-vers">{t.nomVers} →</span>
          </button>
        ))}
      </div>
      {encaissements.sans_date > 0 && (
        <p className="ep-attente">
          {encaissements.sans_date} facture(s) payée(s) sans date de règlement connue chez Pennylane.
        </p>
      )}

      <div className="ep-bloc">
        <div className="ep-bloc-tete">
          <b>Balance âgée</b>
          <span>{euros(cp.total_centimes)} dû au total</span>
        </div>
        {cp.total_centimes > 0 && (
          <div className="ep-barre" role="img" aria-label="Balance âgée">
            {tranches.map((t, i) =>
              t.centimes > 0 ? (
                <div key={t.nom} title={`${t.nom} : ${euros(t.centimes)}`} style={{ flex: t.centimes, background: COULEURS_TRANCHES[i] }} />
              ) : null,
            )}
          </div>
        )}
        <div className="ep-tranches">
          {tranches.map((t, i) => (
            <div key={t.nom} className="ep-tranche">
              <span>
                <i style={{ background: COULEURS_TRANCHES[i] }} />
                {t.nom}
              </span>
              <b className={t.centimes === 0 ? 'rp-vide' : ''}>{euros(t.centimes)}</b>
            </div>
          ))}
        </div>
      </div>

      <div className="rp-groupe">Les clients qui doivent le plus d’échu</div>
      {premiers.length === 0 ? (
        <p className="rp-vide-file">Aucun client en retard.</p>
      ) : (
        <div className="rp-cadre">
          <table className="rp-table">
            <thead>
              <tr>
                <th>Client</th>
                <th className="n">Échu</th>
                <th className="n">Plus gros retard</th>
                <th>Dernière relance</th>
              </tr>
            </thead>
            <tbody>
              {premiers.map((c) => (
                <tr key={c.cle} className="ep-cliquable" onClick={() => p.quandAller('poste', c.cle)}>
                  <td className="rp-client">
                    <button
                      className="ep-client"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        p.quandAller('poste', c.cle);
                      }}
                    >
                      {c.nom}
                    </button>
                  </td>
                  <td className="n">
                    <span className="rp-refus">{euros(c.echu_centimes)}</span>
                  </td>
                  <td className="n">{plusGrosRetard(c)} j</td>
                  <td>
                    <Niveau niveau={c.derniere?.niveau ?? null} />
                    {c.derniere !== null && <span className="rp-sous">{dateDite(c.derniere.envoyee_le)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function EcranSuiviPl({
  jeton,
  quandAccesPerdu,
  version,
  quandAller,
}: ProprietesEcran & {
  readonly quandAller: (onglet: 'poste' | 'relances' | 'encaissements', client?: string) => void;
}) {
  const [donnees, setDonnees] = useState<DonneesSuivi | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  const relire = useCallback(async () => {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return;
    }
    const poste = await lirePoste(j);
    if (poste.etat !== 'ok') {
      if (poste.cause === 'acces') quandAccesPerdu();
      setRefus(poste.message ?? 'Le suivi n’a pas pu être lu.');
      return;
    }
    const jour = poste.valeur.jour;
    const debutMois = `${jour.slice(0, 7)}-01`;
    const il30 = decaler(jour, -30);
    const [relances, enc] = await Promise.all([
      lireFileRelances(j),
      lireEncaissements(j, debutMois < il30 ? debutMois : il30, jour),
    ]);
    if (relances.etat !== 'ok' || enc.etat !== 'ok') {
      const cause = relances.etat !== 'ok' ? relances : enc.etat !== 'ok' ? enc : null;
      if (cause?.cause === 'acces') quandAccesPerdu();
      setRefus(cause?.message ?? 'Le suivi n’a pas pu être lu.');
      return;
    }
    setDonnees({ poste: poste.valeur, relances: relances.valeur, encaissements: enc.valeur });
    setRefus(null);
  }, [jeton, quandAccesPerdu]);

  useEffect(() => {
    void relire();
  }, [relire, version]);

  return <VueSuiviPl donnees={donnees} refus={refus} quandAller={quandAller} />;
}
